import { useCallback, useEffect, useRef, useState } from 'react'
import { X, Loader2, CameraOff, Keyboard, Zap, ZapOff, ScanLine } from 'lucide-react'
import { parseScan } from '../../lib/assetScan.js'

/* Full-screen QR scanner.
 *
 * Two decoders, because there is no single one that works everywhere:
 *   · BarcodeDetector — native, zero bundle cost, Chrome/Android.
 *   · jsQR — lazy-imported WASM-free fallback, needed for iOS Safari which
 *     still ships no BarcodeDetector.
 * The jsQR chunk is only fetched on devices that actually need it.
 *
 * Manual entry is always available: some assets are too small or too wet to
 * carry a sticker, so the id gets written on by hand.
 */
export default function QrScanner({ onDetect, onClose, title = 'สแกน QR สินทรัพย์' }) {
  const videoRef = useRef(null)
  const canvasRef = useRef(null)
  const streamRef = useRef(null)
  const rafRef = useRef(0)
  const doneRef = useRef(false)

  const [status, setStatus] = useState('starting') // starting | scanning | error
  const [err, setErr] = useState('')
  const [manual, setManual] = useState(false)
  const [manualVal, setManualVal] = useState('')
  const [torch, setTorch] = useState(false)
  const [canTorch, setCanTorch] = useState(false)
  const [badScan, setBadScan] = useState('')

  const stop = useCallback(() => {
    cancelAnimationFrame(rafRef.current)
    const s = streamRef.current
    if (s) { for (const t of s.getTracks()) { try { t.stop() } catch { /* already gone */ } } }
    streamRef.current = null
  }, [])

  // A hit: parse first, and only tear the camera down when it really is ours —
  // a stray QR on the box (a manufacturer's serial, a WiFi code) should just be
  // ignored so the user can keep aiming.
  const handleText = useCallback((text) => {
    const parsed = parseScan(text)
    if (!parsed) { setBadScan(String(text || '').slice(0, 60)); return }
    setBadScan('')
    if (doneRef.current) return
    doneRef.current = true
    stop()
    onDetect?.(parsed)
  }, [onDetect, stop])

  useEffect(() => {
    let alive = true

    async function run() {
      if (!navigator.mediaDevices?.getUserMedia) {
        setErr('เบราว์เซอร์นี้ใช้กล้องไม่ได้ — กรอกรหัสสินทรัพย์แทนได้'); setStatus('error'); setManual(true)
        return
      }
      let stream
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: 'environment' }, width: { ideal: 1280 }, height: { ideal: 720 } },
          audio: false,
        })
      } catch (e) {
        if (!alive) return
        const name = e?.name || ''
        setErr(
          name === 'NotAllowedError' ? 'ไม่ได้รับอนุญาตให้ใช้กล้อง — เปิดสิทธิ์กล้องให้เว็บนี้ในตั้งค่าเบราว์เซอร์'
          : name === 'NotFoundError' ? 'ไม่พบกล้องบนอุปกรณ์นี้'
          : `เปิดกล้องไม่สำเร็จ: ${e?.message || name || e}`
        )
        setStatus('error'); setManual(true)
        return
      }
      if (!alive) { for (const t of stream.getTracks()) t.stop(); return }
      streamRef.current = stream

      const video = videoRef.current
      if (video) {
        video.srcObject = stream
        video.setAttribute('playsinline', 'true') // iOS: don't hijack into fullscreen
        try { await video.play() } catch { /* autoplay race — the loop still reads frames */ }
      }

      const track = stream.getVideoTracks()[0]
      try { if (track?.getCapabilities?.().torch) setCanTorch(true) } catch { /* not supported */ }

      // pick a decoder
      let detector = null
      try {
        if (window.BarcodeDetector) {
          let formats = null
          try { formats = await window.BarcodeDetector.getSupportedFormats() } catch { formats = null }
          // No format list (older impls) → assume qr_code and let the ctor object.
          if (!Array.isArray(formats) || formats.includes('qr_code')) {
            detector = new window.BarcodeDetector({ formats: ['qr_code'] })
          }
        }
      } catch { detector = null }

      let jsQR = null
      if (!detector) {
        try { jsQR = (await import('jsqr')).default }
        catch { if (alive) { setErr('โหลดตัวอ่าน QR ไม่สำเร็จ — กรอกรหัสสินทรัพย์แทนได้'); setStatus('error'); setManual(true) } return }
      }
      if (!alive) return
      setStatus('scanning')

      // Throttle to ~8 fps: decoding every animation frame burns battery on a
      // phone held up to a machine for a whole check round, and adds nothing.
      let last = 0
      const tick = async (ts) => {
        rafRef.current = requestAnimationFrame(tick)
        if (doneRef.current || !alive) return
        if (ts - last < 120) return
        last = ts
        const v = videoRef.current
        if (!v || v.readyState < 2 || !v.videoWidth) return

        try {
          if (detector) {
            const hits = await detector.detect(v)
            if (hits?.length) handleText(hits[0].rawValue)
            return
          }
          const c = canvasRef.current
          if (!c) return
          // Downscale: jsQR is pure JS, full-res frames are far too slow.
          const w = 480
          const h = Math.round((v.videoHeight / v.videoWidth) * w) || 360
          if (c.width !== w || c.height !== h) { c.width = w; c.height = h }
          const ctx = c.getContext('2d', { willReadFrequently: true })
          ctx.drawImage(v, 0, 0, w, h)
          const img = ctx.getImageData(0, 0, w, h)
          const hit = jsQR(img.data, w, h, { inversionAttempts: 'dontInvert' })
          if (hit?.data) handleText(hit.data)
        } catch { /* a dropped frame is not an error worth showing */ }
      }
      rafRef.current = requestAnimationFrame(tick)
    }

    run()
    return () => { alive = false; stop() }
  }, [handleText, stop])

  // The "not one of ours" hint is transient — clear it so it doesn't linger
  // while the user re-aims at the right sticker.
  useEffect(() => {
    if (!badScan) return
    const t = setTimeout(() => setBadScan(''), 2500)
    return () => clearTimeout(t)
  }, [badScan])

  // Escape closes
  useEffect(() => {
    function onKey(e) { if (e.key === 'Escape') { stop(); onClose?.() } }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose, stop])

  async function toggleTorch() {
    const track = streamRef.current?.getVideoTracks?.()[0]
    if (!track) return
    const next = !torch
    try { await track.applyConstraints({ advanced: [{ torch: next }] }); setTorch(next) }
    catch { setCanTorch(false) }
  }

  function submitManual(e) {
    e.preventDefault()
    const parsed = parseScan(manualVal)
    if (!parsed) { setErr('กรอกเป็นตัวเลขรหัสสินทรัพย์ เช่น 128'); return }
    doneRef.current = true
    stop()
    onDetect?.(parsed)
  }

  return (
    <div className="fixed inset-0 z-50 bg-slate-950 flex flex-col">
      {/* header */}
      <div className="flex items-center justify-between gap-2 px-4 py-3 text-white shrink-0">
        <div className="flex items-center gap-2 text-[15px] font-semibold"><ScanLine size={18} /> {title}</div>
        <div className="flex items-center gap-1">
          {canTorch && (
            <button onClick={toggleTorch} className="p-2 rounded-lg text-white/80 hover:bg-white/10" title="ไฟฉาย">
              {torch ? <Zap size={18} /> : <ZapOff size={18} />}
            </button>
          )}
          <button onClick={() => { stop(); onClose?.() }} className="p-2 rounded-lg text-white/80 hover:bg-white/10" title="ปิด"><X size={20} /></button>
        </div>
      </div>

      {/* viewfinder */}
      <div className="relative flex-1 min-h-0 overflow-hidden">
        <video ref={videoRef} muted playsInline className="absolute inset-0 w-full h-full object-cover" />
        <canvas ref={canvasRef} className="hidden" />

        {status === 'scanning' && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className="w-[68vw] max-w-[300px] aspect-square rounded-3xl ring-4 ring-white/90 shadow-[0_0_0_100vmax_rgba(2,6,23,0.55)]" />
          </div>
        )}
        {status === 'starting' && (
          <div className="absolute inset-0 flex items-center justify-center text-white/70 text-[14px]">
            <Loader2 size={18} className="animate-spin mr-2" /> กำลังเปิดกล้อง…
          </div>
        )}
        {status === 'error' && (
          <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-8">
            <CameraOff size={32} className="text-white/40 mb-3" />
            <p className="text-[13.5px] text-white/70 leading-relaxed">{err}</p>
          </div>
        )}
      </div>

      {/* footer */}
      <div className="shrink-0 px-5 pt-3 pb-6 text-center">
        {badScan && status === 'scanning' && (
          <p className="text-[12px] text-amber-300/90 mb-2">อ่านได้แต่ไม่ใช่ป้ายสินทรัพย์ของระบบ — เล็งที่ QR บนสติกเกอร์</p>
        )}
        {status === 'scanning' && !badScan && (
          <p className="text-[13px] text-white/60 mb-2">เล็ง QR บนสติกเกอร์ให้อยู่ในกรอบ</p>
        )}

        {manual ? (
          <form onSubmit={submitManual} className="flex items-center gap-2 max-w-[360px] mx-auto">
            <input
              autoFocus inputMode="numeric" value={manualVal} onChange={(e) => setManualVal(e.target.value)}
              placeholder="รหัสสินทรัพย์ เช่น 128"
              className="flex-1 rounded-xl bg-white/10 border border-white/20 px-3 py-2.5 text-[14px] text-white placeholder:text-white/40 outline-none focus:border-white/50"
            />
            <button type="submit" className="px-4 py-2.5 rounded-xl text-[14px] font-semibold text-slate-900 bg-white hover:bg-white/90">ไป</button>
          </form>
        ) : (
          <button onClick={() => setManual(true)} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-[13px] font-medium text-white/70 hover:bg-white/10">
            <Keyboard size={14} /> ไม่มีสติกเกอร์ — กรอกรหัสเอง
          </button>
        )}
      </div>
    </div>
  )
}
